import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ICON_OPTIONS = ["Briefcase", "PawPrint", "ShoppingBag", "KeyRound", "FileText", "Car", "Laptop", "Tag", "Bike", "Camera", "Watch", "Gamepad2"];

export default function AdminCategories() {
  const [cats, setCats] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/admin/categories"); setCats(data.data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/categories/${form.id}`, form);
      else await api.post("/admin/categories", form);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-3xl tracking-tight">Categories</h1>
        <button data-testid="add-category-btn" onClick={() => setEdit({ name: "", icon: "Tag", description: "", status: "active" })} className="tap px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2"><Icon name="Plus" className="w-4 h-4" /> New</button>
      </div>

      {cats === null ? <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cats.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border bg-background p-5" data-testid={`category-${c.slug}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center"><Icon name={c.icon} className="w-5 h-5" /></div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${c.status === "active" ? "bg-[#EAF9EF] text-status-found" : "bg-secondary text-muted-foreground"}`}>{c.status}</span>
              </div>
              <p className="font-display font-bold text-lg mt-3">{c.name}</p>
              <p className="text-sm text-muted-foreground line-clamp-1">{c.description}</p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                <span>{c.tag_count} tags</span><span>{c.theme_count} themes</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button data-testid={`edit-category-${c.slug}`} onClick={() => setEdit(c)} className="tap flex-1 py-2 rounded-lg border border-border font-semibold text-sm">Edit</button>
                <button onClick={() => del(c.id)} className="tap px-3 py-2 rounded-lg border border-border text-status-lost"><Icon name="Trash2" className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>}

      <CatDialog data={edit} onClose={() => setEdit(null)} onSave={save} />
    </div>
  );
}

function CatDialog({ data, onClose, onSave }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => setForm(data || {}), [data]);
  if (!data) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} category</DialogTitle></DialogHeader>
        <input data-testid="cat-name" value={form.name || ""} onChange={set("name")} placeholder="Name" className="w-full h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
        <textarea data-testid="cat-desc" value={form.description || ""} onChange={set("description")} placeholder="Description" rows={2} className="w-full p-3 rounded-xl border border-input resize-none focus:outline-none focus:ring-2 focus:ring-ring" />
        <div>
          <span className="text-sm font-semibold">Icon</span>
          <div className="grid grid-cols-6 gap-2 mt-2">
            {ICON_OPTIONS.map((ic) => (
              <button key={ic} onClick={() => set("icon")(ic)} className={`aspect-square rounded-lg grid place-items-center border ${form.icon === ic ? "border-primary ring-2 ring-primary" : "border-border"}`}><Icon name={ic} className="w-5 h-5" /></button>
            ))}
          </div>
        </div>
        <button data-testid="cat-save" onClick={() => onSave(form)} className="tap w-full h-12 rounded-full bg-primary text-primary-foreground font-bold">Save</button>
      </DialogContent>
    </Dialog>
  );
}
